import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Visitor } from "./visitor";
import VisitorTable, { LEVEL_OF_INTEREST } from "./VisitorTable";
import VisitorCreateModal from "./VisitorCreateModal";
import VisitorEditModal from "./VisitorEditModal";
import FiliereManagementModal from "./FiliereManagementModal";
import VisitorStatsDashboard from "./VisitorStatsDashboard";
import {
  exportExcelVisitorsByAcademicYear,
  getAllVisitors,
} from "../../api/visiteurs";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { getAllAcademicYear } from "../../api/generalParam";
import { getAllFilieres } from "../../api/filieres";
import "moment/locale/fr";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";
import moment from "moment";

// bouton protégée pour créer un visiteur
const ProtectedBtnAddVisitor = withPermission(
  (props: any) => (
    <button
      className="btn btn-dark d-flex align-items-center shadow-sm px-3"
      onClick={() => props.setShowCreateModal(true)}
    >
      <i className="ti ti-plus me-2" />
      Nouveau Visiteur
    </button>
  ),
  [buildPermission(Resource.VISITOR, Action.CREATE)],
  "masquer",
);

//bouton pour la gestion des filières
const ProtectedBtnManageFiliere = withPermission(
  (props: any) => (
    <button
      className="btn btn-outline-primary d-flex align-items-center shadow-sm px-3"
      onClick={() => props.setShowFiliereModal(true)}
    >
      <i className="ti ti-settings me-2" />
      Gérer les Filières
    </button>
  ),
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.FILIERE, action),
    ),
  ],
  "masquer",
);

export default function VisitorsPage() {
  const { selectedYear } = useAcademicYear();
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [filieres, setFilieres] = useState<any[]>([]);
  const [academicYears, setAcademicYears] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter states
  const [selectedFiliere, setSelectedFiliere] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedYearFilter, setSelectedYearFilter] = useState<string[]>([]);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [selectedRegisterStatut, setSelecetedRegisterStatut] = useState<
    boolean | null
  >(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFiliereModal, setShowFiliereModal] = useState(false);
  const [editVisitor, setEditVisitor] = useState<Visitor | null>(null);
  const [showStatsDashboard, setShowStatsDashboard] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [visitorsData, yearsData, fieldsData] = await Promise.all([
        getAllVisitors(),
        getAllAcademicYear(),
        getAllFilieres(buildPermissionContext(Resource.VISITOR, Action.READ)),
      ]);
      // console.log(
      //   "Allvisitor: ",
      //   visitorsData,
      //   "\n All YearData: ",
      //   yearsData,
      //   "\n All FiliereData: ",
      //   fieldsData,
      // );
      setVisitors(visitorsData.data || []);
      setAcademicYears(yearsData.data || []);
      setFilieres(fieldsData.data || []);
    } catch (error: any) {
      // console.error("Erreur lors du chargement des données:", error);
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const levelOptions = [
    { id: "Baccalauréat", label: "Baccalauréat" },
    { id: "Licence 1", label: "Licence 1" },
    { id: "Licence 2", label: "Licence 2" },
    { id: "Licence 3", label: "Licence 3" },
    { id: "Master 1", label: "Master 1" },
    { id: "Master 2", label: "Master 2" },
  ];

  const filteredVisitors = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return visitors.filter((v) => {
      // 1. Match Search Term (Nom, Prénom, Email, date)
      const date = v.createdAt
        ? moment(v.createdAt).format("LL")
        : moment().format("LL");

      const matchSearch =
        query.length === 0 ||
        `${v.firstname} ${v.lastname} ${v.email} ${date}`
          .toLowerCase()
          .includes(query) ||
        v.email?.toLowerCase().includes(query);

      // 2. Match Filière (id_filiere)
      const matchFiliere =
        selectedFiliere.length === 0 ||
        selectedFiliere.includes(v.id_filiere || "");

      // 3. Match Niveau d'étude (level_of_study)
      const matchLevel =
        selectedLevels.length === 0 ||
        selectedLevels.includes(v.level_of_study || "");

      // 4. Match Statut d'inscription (registered)
      const matchRegisteredStatut =
        selectedRegisterStatut === null ||
        v.registration_statut === selectedRegisterStatut;

      // 5. Match Année Académique (id_academicYear)
      const matchYear =
        selectedYearFilter.length === 0 ||
        selectedYearFilter.includes(v.id_academicYear || "");

      // 6. Match niveau d'intérêt (level of interest)
      const matchLevelInterest =
        selectedInterests.length === 0 ||
        selectedInterests.includes(v.level_of_interest || "");

      return (
        matchSearch &&
        matchFiliere &&
        matchLevel &&
        matchYear &&
        matchRegisteredStatut &&
        matchLevelInterest
      );
    });
  }, [
    visitors,
    searchTerm,
    selectedFiliere,
    selectedLevels,
    selectedInterests,
    selectedYearFilter,
    selectedRegisterStatut,
  ]);

  const toggleFiliere = (id: string) => {
    setSelectedFiliere((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleLevel = (level: string) => {
    setSelectedLevels((prev) =>
      prev.includes(level)
        ? prev.filter((item) => item !== level)
        : [...prev, level],
    );
  };

  const toggleInterest = (level: string) => {
    setSelectedInterests((prev) =>
      prev.includes(level)
        ? prev.filter((item) => item !== level)
        : [...prev, level],
    );
  };

  const handleExportExcel = () => {
    if (!selectedYear?.id) {
      toast.error("Veuillez sélectionner une année académique");
      return;
    }
    toast.promise(exportExcelVisitorsByAcademicYear(selectedYear?.id), {
      loading: "Exportation en cours...",
      success: (data) => {
        // console.log("data export: ",data)
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);

        // Créer un lien temporaire
        const a = document.createElement("a");
        a.href = url;

        a.download = `visiteurs_${selectedYear?.label}.xlsx`;
        a.click();
        return "Exporté avec succès";
      },
      error: (error: any) => {
        // console.error("Error:export visiteur Excel ", error);
        return (
          error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard"
        );
      },
    });
  };

  const toggleYear = (id: string) => {
    setSelectedYearFilter((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleCreateSuccess = (newVisitor: Visitor) => {
    setVisitors((prev) => [newVisitor, ...prev]);
  };

  const handleUpdateSuccess = (updated: Visitor) => {
    setVisitors((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1 text-dark fw-bold">
              Gestion des Visiteurs
            </h3>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <ProtectedBtnManageFiliere
              setShowFiliereModal={setShowFiliereModal}
            />
            <ProtectedBtnAddVisitor setShowCreateModal={setShowCreateModal} />
          </div>
        </div>

        <div
          className="bg-white p-3 rounded-3 shadow-sm d-flex align-items-center justify-content-between flex-wrap mb-4"
          style={{ border: "1px solid #eef0f2" }}
        >
          <div className="d-flex align-items-center flex-wrap gap-2">
            {/* Search box */}
            <div className="search-box" style={{ minWidth: "220px" }}>
              <div className="input-group input-group-flat border rounded-2">
                <span className="input-group-text bg-transparent border-0 pe-1">
                  <i className="ti ti-search text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-0 ps-1 py-2"
                  placeholder="Rechercher un nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div
              className="divider-v mx-2"
              style={{ height: "24px", borderLeft: "1px solid #eef0f2" }}
            ></div>

            {/* Filières Filter */}
            <div className="dropdown">
              <button
                className="btn btn-outline-light border text-dark d-flex align-items-center gap-2 px-3 py-2 rounded-2"
                data-bs-toggle="dropdown"
              >
                <i className="ti ti-category fs-16 text-muted"></i>
                {selectedFiliere.length > 0
                  ? `${selectedFiliere.length} Filière(s)`
                  : "Filières"}
              </button>
              <ul
                className="dropdown-menu p-2 shadow-lg border-0"
                style={{ minWidth: "220px" }}
                onClick={(e) => e.stopPropagation()}
              >
                {filieres.map((f) => (
                  <li key={f.id} className="mb-1">
                    <label
                      className={`dropdown-item d-flex align-items-center gap-2 rounded-1 ${selectedFiliere.includes(f.id) ? "bg-light text-primary" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        checked={selectedFiliere.includes(f.id)}
                        onChange={() => toggleFiliere(f.id)}
                      />
                      <span>{f.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* Niveaux Filter */}
            <div className="dropdown">
              <button
                className="btn btn-outline-light border text-dark d-flex align-items-center gap-2 px-3 py-2 rounded-2"
                data-bs-toggle="dropdown"
              >
                <i className="ti ti-school fs-16 text-muted"></i>
                {selectedLevels.length > 0
                  ? `${selectedLevels.length} Niveau(x)`
                  : "Niveaux"}
              </button>
              <ul
                className="dropdown-menu p-2 shadow-lg border-0"
                style={{ minWidth: "200px" }}
                onClick={(e) => e.stopPropagation()}
              >
                {levelOptions.map((l) => (
                  <li key={l.id} className="mb-1">
                    <label
                      className={`dropdown-item d-flex align-items-center gap-2 rounded-1 ${selectedLevels.includes(l.id) ? "bg-light text-primary" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        checked={selectedLevels.includes(l.id)}
                        onChange={() => toggleLevel(l.id)}
                      />
                      <span>{l.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* Années Filter */}
            <div className="dropdown">
              <button
                className="btn btn-outline-light border text-dark d-flex align-items-center gap-2 px-3 py-2 rounded-2"
                data-bs-toggle="dropdown"
              >
                <i className="ti ti-calendar fs-16 text-muted"></i>
                {selectedYearFilter.length > 0
                  ? `${selectedYearFilter.length} Année(s)`
                  : "Années"}
              </button>
              <ul
                className="dropdown-menu p-2 shadow-lg border-0"
                style={{ minWidth: "180px" }}
                onClick={(e) => e.stopPropagation()}
              >
                {academicYears.map((y) => (
                  <li key={y.id} className="mb-1">
                    <label
                      className={`dropdown-item d-flex align-items-center gap-2 rounded-1 ${selectedYearFilter.includes(y.id) ? "bg-light text-primary" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        checked={selectedYearFilter.includes(y.id)}
                        onChange={() => toggleYear(y.id)}
                      />
                      <span>{y.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* Statut d'inscription Filter */}
            <div className="dropdown">
              <button
                className="btn btn-outline-light border text-dark d-flex align-items-center gap-2 px-3 py-2 rounded-2"
                data-bs-toggle="dropdown"
              >
                <i className="ti ti-user-check fs-16 text-muted"></i>
                Statuts d'inscription
              </button>
              <ul
                className="dropdown-menu p-2 shadow-lg border-0"
                style={{ minWidth: "200px" }}
                onClick={(e) => e.stopPropagation()}
              >
                <li>
                  <label
                    className={`dropdown-item d-flex align-items-center gap-2 rounded-1 ${selectedRegisterStatut ? "bg-light text-primary" : ""}`}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={selectedRegisterStatut === true}
                      onChange={() =>
                        setSelecetedRegisterStatut((prev) =>
                          prev === true ? null : true,
                        )
                      }
                    />
                    <span>Inscrit</span>
                  </label>
                </li>
                <li>
                  <label
                    className={`dropdown-item d-flex align-items-center gap-2 rounded-1 ${selectedRegisterStatut === false ? "bg-light text-primary" : ""}`}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={selectedRegisterStatut === false}
                      onChange={() =>
                        setSelecetedRegisterStatut((prev) =>
                          prev === false ? null : false,
                        )
                      }
                    />
                    <span>Non Inscrit</span>
                  </label>
                </li>
              </ul>
            </div>

            {/* Interext level Filter */}
            <div className="dropdown">
              <button
                className="btn btn-outline-light border text-dark d-flex align-items-center gap-2 px-3 py-2 rounded-2"
                data-bs-toggle="dropdown"
              >
                <i className="ti ti-clipboard-check fs-16 text-muted"></i>
                {selectedInterests.length > 0
                  ? `${selectedInterests.length} Niveau d'interêt(s)`
                  : "Niveau d'intérêt"}
              </button>
              <ul
                className="dropdown-menu p-2 shadow-lg border-0"
                style={{ minWidth: "180px" }}
                onClick={(e) => e.stopPropagation()}
              >
                {LEVEL_OF_INTEREST.map((lvi, idx) => (
                  <li key={idx} className="mb-1">
                    <label
                      className={`dropdown-item d-flex align-items-center gap-2 rounded-1 ${selectedInterests.includes(lvi.label) ? "bg-light text-primary" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        checked={selectedInterests.includes(lvi.label)}
                        onChange={() => toggleInterest(lvi.label)}
                      />
                      <span>{lvi.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3">
            <button
              className={`btn btn-icon btn-sm ${showStatsDashboard ? "btn-outline-primary" : "btn-outline-light"}`}
              onClick={() => setShowStatsDashboard((prev) => !prev)}
              title={`${showStatsDashboard ? "Masquer" : "Afficher"} Statistique`}
            >
              <i
                className={`ti ti-chart-${showStatsDashboard ? "bar" : "pie"}`}
              />
            </button>
            <button
              className="btn btn-outline-light border text-dark d-flex align-items-center gap-2 px-3 py-2 rounded-2"
              onClick={handleExportExcel}
            >
              <i className="ti ti-upload fs-16 text-muted"></i>
              Exporter
            </button>
          </div>
        </div>

        {showStatsDashboard && (
          <VisitorStatsDashboard visitors={filteredVisitors} />
        )}

        <div className="card shadow-none border-0 bg-transparent">
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-3 d-flex align-items-center justify-content-between">
                  <h5 className="fw-bold mb-0 text-dark">
                    {filteredVisitors.length} Visiteurs
                  </h5>
                </div>
                <VisitorTable
                  visitors={filteredVisitors}
                  filieres={filieres}
                  onUpdate={loadData}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <VisitorCreateModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        filieres={filieres}
      />

      <VisitorEditModal
        show={showEditModal}
        visitor={editVisitor}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleUpdateSuccess}
        filieres={filieres}
      />

      <FiliereManagementModal
        show={showFiliereModal}
        onClose={() => setShowFiliereModal(false)}
        onSaved={loadData}
      />
    </div>
  );
}
